import React, { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Typography,
} from '@mui/material';
import addBlockIcon from '@src/assets/icons/add-block.svg';
import CrownImg from '@src/assets/images/crown.png';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { LANDING_PAGE_URL } from '@src/configs';
import apis from '@src/apis';
import { registerRequestMeta } from '@src/services/moengage';
import {
  pickSubmittedVoice,
  trackTryVoice,
  TRY_VOICE_ACTION,
} from '@src/services/voiceTracking';
import {
  MOE_MODULE,
  MOE_PAYWALL_TRIGGER,
  MOE_PAYWALL_TYPE,
} from '@src/constants/moengage';
import usePaywallTracking from '@src/hooks/usePaywallTracking';
import {
  SIDEBAR_WIDTH_COLLAPSED,
  FLASH_SALE_BANNER_HEIGHT,
} from '@src/styles/config';
import {
  CONVERTING_STATUS,
  DIRECTION,
  MAX_CHARACTER_PER_BLOCK,
  MAX_TOTAL_CHARACTERS,
  HEADER_HEIGHT,
  AUDIO_PLAYER_HEIGHT,
  FETCH_BLOCKS_STATUS_INTERVAL,
  TOOLBAR_HEIGHT,
  BLOCK_STATUS,
  SYNTHESIS_CONTEXT,
  AUTO_SAVE_STATUS,
  CHARACTER_LIMIT_BANNER_HEIGHT,
  EXPORT_BANNER_HEIGHT,
  PROJECT_PRODUCT,
} from '@src/constants/project';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import { useHistory } from 'react-router-dom';
import { updateProject } from '@src/apis/project';
import { checkFeaturePermission, countTextLength } from '@src/services/tts';
import {
  DEFAULT_BREAK_TIME,
  DEFAULT_SPEED,
  VOICE_OWNERSHIP,
  // OUTPUT_AUDIO,
} from '@src/constants/voice';
import actions from '@src/redux/actions';
import { ERROR_CODE } from '@src/errors/code';
import { useAudioPlayerMultiBlock } from '@src/hooks/useAudioPlayerMultiBlock';
import useUnloadGuard from '@src/hooks/useUnloadGuard';
import useAutoSaveFlush from '@src/hooks/useAutoSaveFlush';
import useIsAutoSaveOverLimit from '@src/hooks/useIsAutoSaveOverLimit';
import CommonButton from '@src/new-components/Button';
import TurnstileWidget from '@src/components/TurnstileWidget';
import {
  FREE_PACKAGE_CODES,
  PACKAGE_LEVEL_V3,
  PACKAGE_STUDIO_VERSION,
} from '@src/constants/package';
import {
  checkCustomPackage,
  checkShouldNotifyLowCredits,
} from '@src/services/package';
import OutOfCreditsCustom from '@src/containers/TTSNew/OutOfCreditsAlert/OutOfCreditsCustom';
import OutOfCreditsVip from '@src/containers/TTSNew/OutOfCreditsAlert/OutOfCreditsVip';
import OutOfCreditsFree from '@src/containers/TTSNew/OutOfCreditsAlert/OutOfCreditsFree';
import useUserInfo from '@src/hooks/useTtsUser';
import { DIALOG_V2_TYPE, CAPTCHA_PROVIDER } from '@src/constants';
import FreeMaxCharsLimitExceeded from '@src/containers/TTSNew/MaxCharsLimitExceeded/FreeMaxCharsLimitExceeded';
import PaidMaxCharsLimitExceeded from '@src/containers/TTSNew/MaxCharsLimitExceeded/PaidMaxCharsLimitExceeded';
import WaringImg from '@src/assets/images/warning.png';
import { delimitNumber } from '@src/utils/number';
import NotificationDialogV4 from '@src/components/NotificationDialogV4';
import sanitizeHtml from 'sanitize-html';
import LockCycle from '@src/assets/images/lock-cycle.svg';
import ROUTES from '@src/constants/route';
import { INIT_NOTIFICATION_DIALOG } from '@src/constants/dialog';
import ExpiredPackageAlert from '@src/containers/TTSNew/ExpiredPackageAlert';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import OutOfCreditsStandard from '@src/containers/TTSNew/OutOfCreditsAlert/OutOfCreditsStandard';
import OutOfCreditsSpecial from '@src/containers/TTSNew/OutOfCreditsAlert/OutOfCreditsSpecial';
import useSynthesisValidation from '@src/hooks/useSynthesisValidation';
import NotificationDialog from '@src/components/NotificationDialog';
import {
  audioExpired,
  checkBlocksChanged,
  isBlockChanged,
  getBlockWithAudio,
  getBlockStatus,
  checkBlockTextError,
  getExportingState,
} from '@src/services/project';
import { containsBlacklistedWords } from '@src/services/blackword';
import { COLOR } from '@src/styles/color';
import {
  buildUpdatedTrialUsage,
  isUsingTrialVoice,
  willExceedTrialLimit,
} from '@src/services/voiceTrial';
import NotifyLowCredits from '@src/containers/TTSNew/NotifyLowCredits';
import useTotalCredits from '@src/hooks/useTotalCredits';
import { getUpgradePackageLabelKey } from '@src/services/voice';
import { StyledActionWarning, StyledContent } from './index.style';
import VirtualizedContentList from './VirtualizedContentList';
import Toolbar from '../Toolbar';
import CustomActionLeavePage from '../../component/CustomActionLeavePage';
import AudioPlayer from '../../component/AudioPlayer';
import AutoSaveBanner from '../../component/AutoSaveBanner';
import ExportingBanner from '../../component/ExportingBanner';

const Content = () => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const history = useHistory();
  const addBlockRef = useRef({});
  const dispatch = useDispatch();
  const { openSidebar } = useSelector((state) => state.layout);
  const { headerVoucher } = useSelector((state) => state.voucher);

  // Fields, not the object: credit updates replace `auth.user` several times a second.
  const userId = useSelector((state) => state.auth.user?.id);
  const userEmail = useSelector((state) => state.auth.user?.email);
  const userPhoneNumber = useSelector((state) => state.auth.user?.phoneNumber);
  const userPackageCode = useSelector((state) => state.auth.user?.packageCode);
  const user = useMemo(
    () => ({
      id: userId,
      email: userEmail,
      phoneNumber: userPhoneNumber,
      packageCode: userPackageCode,
    }),
    [userId, userEmail, userPhoneNumber, userPackageCode],
  );
  const {
    multiBlock,
    projectId,
    title,
    voiceDefault,
    currentBlockId,
    isDownloading,
    isProjectSynthesizing,
    originalTitle,
    isPlayingBlock,
    currentPlayingBlockId,
    openDialogChangeFormat,
    audioType,
    originalAudioType,
    currentFilteringStatus,
    startFilterBlock,
    selectedBlockIds,
    currentFilteringVoiceCode,
  } = useSelector((state) => state.project);
  const { usingPackage, trialVoiceUsage: stateTrialVoiceUsage } = useSelector(
    (state) => state.user,
  );
  const allPackages = useSelector((state) => state.packages.packages);
  const { ttsUser } = useUserInfo();
  const { totalCredits } = useTotalCredits();
  const totalCreditsRef = useRef(totalCredits);
  useEffect(() => {
    totalCreditsRef.current = totalCredits || 0;
  }, [totalCredits]);
  const turnstileWidgetRef = useRef(null);

  const { blocks, blockIds, originalBlocks, originalBlockIds } = multiBlock;
  const isChangedTitle = originalTitle !== title;
  const isChangedAudioType = originalAudioType !== audioType;
  const hasBlockChanged = checkBlocksChanged({
    blocks,
    originalBlocks,
    blockIds,
    originalBlockIds,
  });
  const isDraft = hasBlockChanged || isChangedTitle || isChangedAudioType;
  const blockIdsRef = useRef(blockIds);
  const blocksRef = useRef(blocks);
  const pendingBlockAdditions = useRef(new Set());
  const currentFilteringStatusRef = useRef(currentFilteringStatus);
  const currentFilteringVoiceCodeRef = useRef(currentFilteringVoiceCode);

  const [characterExceed, setCharacterExceed] = useState(false);
  const [openOutOfCreditsDialog, setOpenOutOfCreditsDialog] = useState(false);
  const [warningOneTimeCredits, setWarningOneTimeCredits] = useState(false);
  const [openWrongSyntaxDialogV2, setOpenWrongSyntaxDialogV2] = useState(null);
  const [charsFactorCheckbox, setCharsFactorCheckbox] = useState(false);
  const [openWrongSyntaxDialog, setOpenWrongSyntaxDialog] = useState(null);
  const [openExpiredPackageDialog, setOpenExpiredPackageDialog] =
    useState(false);
  const [openMaxCharsLimitExceeded, setOpenMaxCharsLimitExceeded] =
    useState(false);
  const [openMaxCharsExceededOnSave, setOpenMaxCharsExceededOnSave] =
    useState(false);
  const [openDialogLockedCycle, setOpenDialogLockedCycle] = useState(null);
  const [queueSynthesis, setQueueSynthesis] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [scrollToBlockId, setScrollToBlockId] = useState(null);
  const [isSynthesisMultiBlock, setIsSynthesisMultiBlock] = useState(false);
  const [failedBlocksWithError, setFailedBlocksWithError] = useState([]);
  const [filteredBlockIds, setFilteredBlockIds] = useState(blockIds);
  const [filterBlockResults, setFilterBlockResults] = useState(blocks);
  const [deleteDialogData, setDeleteDialogData] = useState(false);
  const [notifyLowCreditsDialog, setNotifyLowCreditsDialog] = useState(false);
  const [blackWordsList, setBlackWordsList] = useState([]);
  const [blockBlackWordsMap, setBlockBlackWordsMap] = useState({});

  const packageCode = usingPackage?.code;
  const { trackHit: trackPaywall } = usePaywallTracking({
    module: MOE_MODULE.PROJECT,
    product: PROJECT_PRODUCT.TTS,
    projectId,
    trigger: MOE_PAYWALL_TRIGGER.CONVERT,
  });
  const maxLengthInputText = ttsUser?.maxLengthInputText || 0;

  const { getFeatureValue } = useFeatureFlags();

  const {
    handlePlay,
    handlePause,
    handleSeeked,
    isLoading,
    isPlaying,
    currentTime,
    duration,
    handleChangeVolume,
    handlePlayAudioInBlock,
    handleLoadAudio,
    blockAudioCurrentTime,
    blockAudioDuration,
    handleSeekedInBlock,
    setAudioResumeBlock,
  } = useAudioPlayerMultiBlock();

  const { validateSynthesis } = useSynthesisValidation();

  const [isOpenUnsavedChangesDialog, setIsOpenUnsavedChangesDialog] =
    useState(false);

  const voiceTrialConfig = getFeatureValue(FEATURE_KEYS.TRIAL_VOICE_CONFIG, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
    packageCode: user.packageCode,
  });
  const enableVoiceTrial = Boolean(voiceTrialConfig);

  const captchaProvider = getFeatureValue(
    FEATURE_KEYS.CAPTCHA_PROVIDER_AIVOICE,
    { userId: user.id, email: user.email },
  );

  const cloudflareTurnstileEnabled = getFeatureValue(
    FEATURE_KEYS.CLOUDFLARE_TURNSTILE_AIVOICE,
    { userId: user.id, email: user.email },
  );

  const enableCreditUsagePreview = getFeatureValue(
    FEATURE_KEYS.ENABLE_CREDIT_USAGE_PREVIEW,
    { userId: user.id, email: user.email },
  );

  const isBlockUIImprovement = getFeatureValue(
    FEATURE_KEYS.PROJECT_TTS_IMPROVE_BLOCK_UI,
    { userId: user.id, email: user.email },
  );

  const autoSaveEnabled = getFeatureValue(FEATURE_KEYS.PROJECT_AUTO_SAVE, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });
  const autoSaveStatus = useSelector((state) => state.project.autoSaveStatus);

  useEffect(() => {
    dispatch(actions.project.setAutoSaveEnabled(autoSaveEnabled));
    return () => dispatch(actions.project.setAutoSaveEnabled(false));
  }, [autoSaveEnabled]);

  // Warn before closing/reloading the tab while a save is in flight or failed.
  useUnloadGuard({ enabled: autoSaveEnabled });
  const { flushAndWaitForSave } = useAutoSaveFlush();
  // Over the character limit under auto-save → project can't be saved, so
  // DB-touching synthesis actions must be disabled.
  const isAutoSaveOverLimit = useIsAutoSaveOverLimit();

  // True while leaving the project could lose unsaved work. Under auto-save the
  // store is the source of truth (SAVING/ERROR); otherwise fall back to the
  // legacy draft detection.
  const isAutoSaveInFlight = [
    AUTO_SAVE_STATUS.SAVING,
    AUTO_SAVE_STATUS.ERROR,
  ].includes(autoSaveStatus);
  const hasUnsavedWork = autoSaveEnabled ? isAutoSaveInFlight : isDraft;

  const exports = useSelector((state) => state.project.exports);
  const exportHistoryEnabled = useExportHistoryEnabled();
  const exportBannerEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_EXPORT_VIDEO,
    { userId: user.id, email: user.email, phoneNumber: user.phoneNumber },
  );
  const { isExporting, formats: exportFormats } = getExportingState({
    exports,
    isDownloading,
    product: PROJECT_PRODUCT.TTS,
  });
  const isAutoSaveBannerVisible =
    autoSaveEnabled &&
    (isAutoSaveOverLimit || autoSaveStatus === AUTO_SAVE_STATUS.DELETED);
  // The banner only exists to explain why editing is blocked, so it goes with
  // the lock: no lock, nothing to explain. `showExportBanner` also reserves the
  // banner's height, so gating it here keeps the layout from leaving a gap.
  const showExportBanner =
    exportBannerEnabled &&
    isExporting &&
    !isAutoSaveBannerVisible &&
    !exportHistoryEnabled;
  // Exporting no longer locks the project. The backend snapshots the project
  // when the export starts, so edits made while it renders cannot change the
  // file. The banner still reports the export; only the lock is gone. Behind
  // the export-history flag, so the previous behaviour is one switch away.
  const isEditLocked =
    isDownloading ||
    (exportBannerEnabled && isExporting && !exportHistoryEnabled);

  const maxCharLimitPerBlock = MAX_CHARACTER_PER_BLOCK;
  const handleCloseExpiredPackageDialog = () => {
    setOpenExpiredPackageDialog(false);
  };
  const openLimitedFeatureDialog = (
    featureKey,
    paywallType,
    paywallVoiceCode,
  ) => {
    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('limitedFeatureDesc', { feature: t(featureKey) }),
        paywall: {
          type: paywallType,
          voiceCode: paywallVoiceCode,
          featureKey,
        },
      }),
    );
  };
  const handleOpenVoiceTrialLimitDialog = (limitedVoices) => {
    const voices = (
      Array.isArray(limitedVoices) ? limitedVoices : [limitedVoices]
    ).filter(Boolean);

    if (!voices.length) return;

    const packageName = t(
      getUpgradePackageLabelKey({
        voices,
        userFeatures: ttsUser?.features,
        packages: allPackages,
      }),
    );

    const isMultipleVoices = voices.length > 1;
    const voiceNamesList = voices
      .map((voice) => voice?.name)
      .filter(Boolean)
      .map((name) => `<li><b>${sanitizeHtml(name)}</b></li>`)
      .join('');

    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: isMultipleVoices
          ? t('trialVoiceLimitDescriptionMultiple', {
              voiceNames: voiceNamesList,
              packageName,
            })
          : t('trialVoiceLimitDescription', {
              voiceName: voices[0]?.name || '',
              packageName,
            }),
        img: CrownImg,
        classNameImg: 'custom-img',
        maxWidth: '640px',
        paywall: {
          type: MOE_PAYWALL_TYPE.VOICE_TRIAL_LIMIT,
          voiceCode: voices
            .map((v) => v?.code)
            .filter(Boolean)
            .join(','),
        },
        onUpgrade: () => {
          history.push(ROUTES.PAYMENT);
          dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
        },
      }),
    );
  };

  const getTrialUsageCandidatesForBlocks = (blocksList = []) => {
    if (!enableVoiceTrial || !Array.isArray(blocksList)) return [];

    const voiceCreditsMap = {};

    blocksList.forEach((blockItem) => {
      if (!blockItem?.id) return;
      const voice = blocksRef.current?.[blockItem.id]?.voice;
      if (!voice?.code) return;

      const isTrialVoice = isUsingTrialVoice(
        voice,
        voiceTrialConfig,
        ttsUser?.features,
        checkFeaturePermission,
      );

      if (!isTrialVoice) return;

      const text =
        blockItem?.text ?? blocksRef.current?.[blockItem.id]?.text ?? '';
      const characters = countTextLength(text || '');
      const creditFactor = voice?.creditFactor || 1;
      const credits = characters * creditFactor;

      if (!voiceCreditsMap[voice.code]) {
        voiceCreditsMap[voice.code] = {
          voice,
          credits: 0,
          times: 0,
        };
      }

      voiceCreditsMap[voice.code].credits += credits;
      voiceCreditsMap[voice.code].times += 1;
    });

    return Object.values(voiceCreditsMap);
  };

  const findExceededTrialVoicesForBlocks = (
    blocksList = [],
    usage = stateTrialVoiceUsage,
  ) => {
    if (!enableVoiceTrial) return [];

    const candidates = getTrialUsageCandidatesForBlocks(blocksList);
    if (!candidates.length) return [];

    return candidates.filter(({ voice, credits, times }) =>
      willExceedTrialLimit(voice, voiceTrialConfig, usage, credits, times),
    );
  };

  const updateTrialUsageAfterSuccess = (blocksList = [], requestIds = []) => {
    if (!enableVoiceTrial) return;
    const candidates = getTrialUsageCandidatesForBlocks(blocksList);
    if (!candidates.length) return;

    if (requestIds.length) {
      requestIds.forEach((requestId) => {
        if (requestId) {
          dispatch(
            actions.synthesisRequest.setSynthesizingVoice(
              requestId,
              candidates,
            ),
          );
        }
      });
    }

    const updatedTrialUsage = buildUpdatedTrialUsage({
      currentUsage: stateTrialVoiceUsage,
      voices: candidates,
      config: voiceTrialConfig,
      userFeatures: ttsUser?.features,
    });

    if (updatedTrialUsage) {
      dispatch(actions.user.updateTrialVoiceUsage(updatedTrialUsage));
    }
  };
  const handleCloseMaxCharsLimitExceededDialog = () =>
    setOpenMaxCharsLimitExceeded(false);
  const isShowHeaderBanner = headerVoucher && headerVoucher.length > 0;
  const blockWithText = Object.keys(blocks)
    .map((blockId) => blocks[blockId])
    .filter((block) => block?.text);
  const totalBlockWithTextAndStatus = blockWithText.length;
  // Only get blocks that have text and status is success and audio expired is not expired
  // This is to avoid calculating the progress when the audio is expired, since expired audio will not be allowed
  const blockSuccessSynthesis = Object.values(blocks).filter(
    (block) =>
      block.status === CONVERTING_STATUS.SUCCESS &&
      !audioExpired(block?.audioExpiredAt),
  );
  const totalBlockSuccessSynthesis = blockSuccessSynthesis.length;
  const synthesisProgress = Math.floor(
    (totalBlockSuccessSynthesis * 100) / totalBlockWithTextAndStatus,
  );
  const isAudioLinkExist = useMemo(
    () =>
      blockWithText.every(
        (block) =>
          block?.audioLink && block?.status === CONVERTING_STATUS.SUCCESS,
      ),
    [blockWithText],
  );

  const blockWithTextChanged = useMemo(
    () =>
      blockWithText.filter((block) => {
        const withoutExtension = block?.audioLink?.split('.')?.pop();
        const currentBlock = {
          text: block?.text,
          voiceCode: block?.voiceCode,
          speed: block?.speed,
          audioType: withoutExtension,
          emphasis: block?.emphasis,
        };
        const originalBlock = {
          text: originalBlocks[block.id]?.text,
          voiceCode: originalBlocks[block.id]?.voiceCode,
          speed: originalBlocks[block.id]?.speed,
          audioType,
          emphasis: originalBlocks[block.id]?.emphasis,
        };
        return (
          isBlockChanged(currentBlock, originalBlock) ||
          !block?.audioLink ||
          audioExpired(block?.audioExpiredAt)
        );
      }),
    [blockWithText, originalBlocks, audioType],
  );

  const synthesisInProgress = blockWithTextChanged.filter(
    (block) => block.status === CONVERTING_STATUS.IN_PROGRESS,
  );

  const isDisableConvertBtn =
    synthesisInProgress?.length === blockWithTextChanged?.length ||
    isAutoSaveOverLimit;

  const hasAudioExpired = blockWithText.some((block) =>
    audioExpired(block?.audioExpiredAt),
  );
  const isSynthesisInProgress =
    blockWithText.filter(
      (block) => block.status === CONVERTING_STATUS.IN_PROGRESS,
    ).length > 0;
  const isDisabledBtnAudioEffects =
    !blockWithText?.length ||
    hasAudioExpired ||
    isSynthesisInProgress ||
    blockWithTextChanged?.length;

  const isMultiBlockProcessing = useMemo(
    () =>
      blockWithText.filter(
        (block) => block.status === CONVERTING_STATUS.IN_PROGRESS,
      ).length > 0,
    [blockWithText],
  );
  const isProjectConverting = isProjectSynthesizing && isMultiBlockProcessing;

  const isFilteringBlockStatus = useMemo(
    () => currentFilteringStatus !== BLOCK_STATUS.ALL,
    [currentFilteringStatus],
  );

  const isFilteringBlockVoice = useMemo(
    () => currentFilteringVoiceCode !== '',
    [currentFilteringVoiceCode],
  );

  const hasSelectedBlock = useMemo(
    () => selectedBlockIds.length > 0,
    [selectedBlockIds],
  );

  const isUsingTurnstile =
    captchaProvider === CAPTCHA_PROVIDER.TURNSTILE &&
    cloudflareTurnstileEnabled;

  const handleCloseOutOfCreditsDialog = () => setOpenOutOfCreditsDialog(false);

  const handleCloseNotificationDialogV2 = () =>
    dispatch(
      actions.dialog.displayDialogV2({
        notificationDialogV2: INIT_NOTIFICATION_DIALOG,
      }),
    );

  const renderButtonUnderstand = () => (
    <Button
      variant="contained"
      onClick={handleCloseNotificationDialogV2}
      sx={{
        float: 'right',
        marginLeft: '-10px',
        marginBottom: '-10px',
        padding: '8px 48px',
        borderRadius: '12px',
        textTransform: 'none',
        fontSize: '14px',
        fontWeight: 700,
      }}
    >
      {t('gotIt')}
    </Button>
  );

  const handleOpenDiscardedVoiceCloningDialog = () => {
    dispatch(
      actions.dialog.displayDialogV2({
        notificationDialogV2: {
          name: 'voice-cloning-discard-info',
          title: t('unavailableVoice'),
          description: t('discardedVoiceCloning', {
            style: 'margin-left: -16px',
          }),
          open: true,
          actionComponent: renderButtonUnderstand(),
          showIcon: false,
          onClose: handleCloseNotificationDialogV2,
          variant: null,
        },
      }),
    );
  };

  const checkValidCreateSynthesis = (blockData) => {
    const validationResult = validateSynthesis({
      isMultiBlock: !blockData,
      onOpenOutOfCreditsDialog: () => setOpenOutOfCreditsDialog(true),
      onOpenExpiredPackageDialog: () => setOpenExpiredPackageDialog(true),
      onOpenDialogLockedCycle: setOpenDialogLockedCycle,
      onOpenMaxCharsLimitExceeded: () => setOpenMaxCharsLimitExceeded(true),
      onOpenLimitedFeatureDialog: openLimitedFeatureDialog,
      onSetWarningOneTimeCredits: setWarningOneTimeCredits,
      onOpenWrongSyntaxDialog: setOpenWrongSyntaxDialog,
      onOpenWrongSyntaxDialogV2: setOpenWrongSyntaxDialogV2,
      onOpenDiscardedVoiceCloning: handleOpenDiscardedVoiceCloningDialog,
      onOpenVoiceTrialLimitDialog: handleOpenVoiceTrialLimitDialog,
      blockData,
    });

    return validationResult;
  };

  const renderMaxCharsLimitExceededDialog = () => {
    const isFreePackage = FREE_PACKAGE_CODES.includes(packageCode);

    if (isFreePackage) {
      return (
        <FreeMaxCharsLimitExceeded
          open={openMaxCharsLimitExceeded}
          onClose={handleCloseMaxCharsLimitExceededDialog}
          maxLengthInputText={maxLengthInputText}
        />
      );
    }

    return (
      <PaidMaxCharsLimitExceeded
        open={openMaxCharsLimitExceeded}
        onClose={handleCloseMaxCharsLimitExceededDialog}
        maxLengthInputText={maxLengthInputText}
      />
    );
  };

  const getSidebarWidth = () => {
    let sidebarWidth = 0;
    sidebarWidth = openSidebar ? 0 : SIDEBAR_WIDTH_COLLAPSED;

    return sidebarWidth;
  };
  const handleStopFocusOnBlock = () => {
    dispatch(actions.project.setCurrentBlockId(null));
  };

  const handleChangeCharacterExceed = (value) => {
    setCharacterExceed(value);
  };

  const handleFocusingBlock = (blockId) => {
    const audioLink = blocks[blockId]?.audioLink;
    dispatch(actions.project.setCurrentBlockId(blockId));
    if (!audioLink) return;
    handleLoadAudio(blockId);
  };

  const scrollToBlock = (blockId) => {
    setScrollToBlockId(blockId);
    // Clear after a short delay to allow the effect to trigger
    setTimeout(() => setScrollToBlockId(null), 200);
  };

  const findBlockWithError = (blocksToCheck = blockWithText) =>
    blocksToCheck.find((block) => {
      const { hasError } = checkBlockTextError(
        block?.text,
        maxCharLimitPerBlock,
        blackWordsList,
        block?.voice || voiceDefault,
      );
      return hasError;
    });

  const computeBlockBlackWordsMap = (blocksToCheck = blockWithText) => {
    const newMap = {};
    blocksToCheck.forEach((block) => {
      const found = blackWordsList.filter((bw) =>
        containsBlacklistedWords({ text: block.text, blackWord: bw }),
      );
      if (found.length > 0) newMap[block.id] = found;
    });
    return newMap;
  };

  const getVoiceExist = async () => {
    const voiceExist = await apis.voices.getVoicesV3({
      voiceOwnership: VOICE_OWNERSHIP.VBEE,
      limit: 1,
    });
    if (!voiceExist.status) return [];
    return voiceExist?.result?.voices;
  };
  const getVoiceDefault = async () => {
    const voiceExist = await getVoiceExist();
    if (voiceExist?.length)
      dispatch(actions.project.setVoiceDefault(voiceExist[0]));
  };

  const saveProject = async () => {
    try {
      // TODO: Handle check if there is any block that is error then disable saving project
      const response = await updateProject(projectId, {
        title,
        blocks: blockIdsRef.current.map((blockId) => ({
          id: blockId,
          title: blocksRef.current[blockId].title,
          characters: countTextLength(blocksRef.current[blockId].text || ''),
          text: blocksRef.current[blockId].text || '',
          voiceCode:
            blocksRef.current[blockId]?.voiceCode || voiceDefault?.code,
          breakTime:
            blocksRef.current[blockId]?.breakTime || DEFAULT_BREAK_TIME,
          speed: blocksRef.current[blockId]?.speed || DEFAULT_SPEED,
          elements: blocksRef.current[blockId]?.elements,
          isSample: blocksRef.current[blockId]?.isSample || false,
          startTime: blocksRef.current[blockId]?.startTime || undefined,
          endTime: blocksRef.current[blockId]?.endTime || undefined,
          ...(blocksRef.current[blockId]?.hasEmphasis
            ? { emphasisIntensity: blocksRef.current[blockId]?.emphasis }
            : {}),
        })),
        audioType,
      });
      return response;
    } catch {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
      return null;
    }
  };

  // Persist the current content before an external action (audio effects,
  // translate, AI generate, leave dialog). Under auto-save, flush the pending
  // auto-save and wait for SAVED; otherwise do the legacy full save. Returns a
  // { status } shape so callers can check success uniformly across both modes.
  const saveProjectBeforeAction = async () => {
    if (!autoSaveEnabled) return saveProject();
    const saved = await flushAndWaitForSave();
    return { status: saved };
  };

  // Ensure pending edits are persisted before a credit-spending synthesis runs.
  // Under auto-save, flush and wait for SAVED; otherwise do a legacy full save.
  // Returns true when it is safe to proceed.
  const ensureSavedBeforeSynthesis = async () => {
    if (autoSaveEnabled) return flushAndWaitForSave();
    const response = await saveProject();
    return Boolean(response?.status);
  };

  useEffect(() => {
    blockIdsRef.current = blockIds;
  }, [blockIds]);

  useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);

  useEffect(() => {
    currentFilteringStatusRef.current = currentFilteringStatus;
  }, [currentFilteringStatus]);

  useEffect(() => {
    currentFilteringVoiceCodeRef.current = currentFilteringVoiceCode;
  }, [currentFilteringVoiceCode]);

  useEffect(() => {
    getVoiceDefault();
  }, []);

  useEffect(() => {
    // Under auto-save the unload warning is handled by useUnloadGuard.
    if (autoSaveEnabled) return undefined;

    const handleBeforeUnload = async (event) => {
      if (isDraft || isProjectConverting) {
        if (!isProjectConverting) {
          saveProject();
        }
        event.preventDefault();
        const e = event;
        e.returnValue = '';
        return undefined;
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [
    autoSaveEnabled,
    isDraft,
    t,
    isOpenUnsavedChangesDialog,
    blockIds,
    blocks,
  ]);

  const onLeavePage = () => {
    setIsOpenUnsavedChangesDialog(false);
    dispatch(actions.project.updateOriginalBlocks());
  };

  const handleCloseUnsavedChangesDialog = () => {
    onLeavePage();
  };

  const handleConvertAudio = async () => {
    const newBlackWordsMap = computeBlockBlackWordsMap();
    setBlockBlackWordsMap(newBlackWordsMap);
    const errorBlock = findBlockWithError();
    if (errorBlock) {
      scrollToBlock(errorBlock.id);
      return;
    }
    dispatch(actions.project.setIsPlaying(false));
    dispatch(actions.project.setIsMultiBlockMode(false));
    dispatch(actions.project.setCurrentBlockId(null));
    dispatch(actions.project.setIsPlayingBlock(false));
    if (!checkValidCreateSynthesis()) return;
    const blockWithoutAudioLink = blockWithTextChanged
      ?.filter((block) => block.status !== CONVERTING_STATUS.IN_PROGRESS)
      ?.map((block) => ({
        text: block.text,
        voiceCode: block.voiceCode || voiceDefault?.code,
        voice: block.voice,
        speed: block.speed || DEFAULT_SPEED,
        audioType,
        id: block.id,
        ...(block?.hasEmphasis ? { emphasisIntensity: block?.emphasis } : {}),
      }));
    const newBlockIds = blockWithoutAudioLink.map((block) => block.id);
    const mapBlockWithStatus = (status) =>
      blockWithoutAudioLink?.map((block) => ({
        id: block.id,
        status,
        audioExpiredAt: null, // Delete the old audioExpiredAt to remove the audio expired icon
      }));

    if (blockWithoutAudioLink.length) {
      dispatch(
        actions.project.updateMultiBlockSynthesisInfo(
          newBlockIds,
          mapBlockWithStatus(CONVERTING_STATUS.IN_PROGRESS),
        ),
      );
      dispatch(actions.project.setIsProjectSynthesizing(true));
      dispatch(
        actions.project.updateBlocksStatus({
          blockIds: newBlockIds,
          blockStatus: BLOCK_STATUS.IN_PROGRESS,
        }),
      );

      const saved = await ensureSavedBeforeSynthesis();
      if (!saved) {
        dispatch(
          actions.project.updateMultiBlockSynthesisInfo(
            newBlockIds,
            mapBlockWithStatus(CONVERTING_STATUS.FAILURE),
          ),
        );
        dispatch(
          actions.project.updateBlocksStatus({
            blockIds: newBlockIds,
            blockStatus: BLOCK_STATUS.FAILURE,
          }),
        );
        return;
      }

      dispatch(actions.project.saveProject());

      const queueItems = blockWithoutAudioLink.map((block) => ({
        ...block,
        synthesisContext: SYNTHESIS_CONTEXT.MULTI_BLOCK,
      }));
      setQueueSynthesis((prev) => [...prev, ...queueItems]);
    }
  };
  const handleConvertBlockSelected = async (blocksSelected) => {
    // Scroll to first block with any error
    const errorBlock = findBlockWithError(blocksSelected);
    if (errorBlock) {
      scrollToBlock(errorBlock?.id);
      return;
    }
    if (!checkValidCreateSynthesis(blocksSelected)) return;
    const blockWithoutAudioLink = blocksSelected
      ?.filter((block) => block.blockStatus !== BLOCK_STATUS.IN_PROGRESS)
      ?.map((block) => ({
        text: block.text,
        voiceCode: block.voiceCode || voiceDefault?.code,
        voice: block.voice,
        speed: block.speed || DEFAULT_SPEED,
        audioType,
        id: block.id,
        ...(block?.hasEmphasis ? { emphasisIntensity: block?.emphasis } : {}),
      }));
    const newBlockIds = blockWithoutAudioLink.map((block) => block.id);
    const mapBlockWithStatus = (status) =>
      blockWithoutAudioLink?.map((block) => ({
        id: block.id,
        status,
        audioExpiredAt: null, // Delete the old audioExpiredAt to remove the audio expired icon
      }));

    if (blockWithoutAudioLink.length) {
      dispatch(
        actions.project.updateMultiBlockSynthesisInfo(
          newBlockIds,
          mapBlockWithStatus(CONVERTING_STATUS.IN_PROGRESS),
        ),
      );
      dispatch(
        actions.project.updateBlocksStatus({
          blockIds: newBlockIds,
          blockStatus: BLOCK_STATUS.IN_PROGRESS,
        }),
      );

      const saved = await ensureSavedBeforeSynthesis();
      if (!saved) {
        dispatch(
          actions.project.updateMultiBlockSynthesisInfo(
            newBlockIds,
            mapBlockWithStatus(CONVERTING_STATUS.FAILURE),
          ),
        );
        dispatch(
          actions.project.updateBlocksStatus({
            blockIds: newBlockIds,
            blockStatus: BLOCK_STATUS.FAILURE,
          }),
        );
        return;
      }

      dispatch(actions.project.saveProject());

      const queueItems = blockWithoutAudioLink.map((block) => ({
        ...block,
        synthesisContext: SYNTHESIS_CONTEXT.MULTI_BLOCK,
      }));
      setQueueSynthesis((prev) => [...prev, ...queueItems]);
    }
  };

  const handleConfirmLeave = async (callback) => {
    if (isProjectConverting) {
      onLeavePage();
      if (callback) callback();
      return;
    }

    const totalChars = blockIdsRef.current.reduce((sum, blockId) => {
      const text = blocksRef.current[blockId]?.text || '';
      return sum + countTextLength(text);
    }, 0);

    if (totalChars > MAX_TOTAL_CHARACTERS) {
      setOpenMaxCharsExceededOnSave(true);
      if (callback) callback(false);
      return;
    }

    const response = await saveProjectBeforeAction();
    if (response?.status) {
      if (isOpenUnsavedChangesDialog) {
        window.location.reload();
      }
      onLeavePage();
    }

    if (callback) callback(response?.status);
  };

  const renderOutOfCreditsDialog = () => {
    const isPackageStandardV3 =
      usingPackage.level === PACKAGE_LEVEL_V3.STANDARD &&
      usingPackage?.version?.includes(PACKAGE_STUDIO_VERSION.V3);

    const isPackageSpecialV3 =
      usingPackage.level === PACKAGE_LEVEL_V3.SPECIAL &&
      usingPackage?.version?.includes(PACKAGE_STUDIO_VERSION.V3);

    const isPackageVipV3 =
      usingPackage.level === PACKAGE_LEVEL_V3.VIP &&
      usingPackage?.version?.includes(PACKAGE_STUDIO_VERSION.V3);

    const isCustomPackage = checkCustomPackage({
      type: usingPackage?.type,
      level: usingPackage.level,
    });

    if (isCustomPackage) {
      return (
        <OutOfCreditsCustom
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          packageCode={packageCode}
        />
      );
    }

    if (isPackageStandardV3) {
      return (
        <OutOfCreditsStandard
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          packageCode={packageCode}
          // voice={voice}
        />
      );
    }

    if (isPackageSpecialV3) {
      return (
        <OutOfCreditsSpecial
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          packageCode={packageCode}
          // voice={voice}
        />
      );
    }

    if (isPackageVipV3)
      return (
        <OutOfCreditsVip
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          warningOneTimeCredits={warningOneTimeCredits}
        />
      );

    // Display this popup when using a free package or a package that is neither standard nor special v3.
    return (
      <OutOfCreditsFree
        open={openOutOfCreditsDialog}
        onClose={handleCloseOutOfCreditsDialog}
      />
    );
  };

  const renderUnsavedChangesActions = () => (
    <StyledActionWarning>
      <Button
        className="btn-non-vbee-warning"
        variant="outlined"
        onClick={handleCloseUnsavedChangesDialog}
      >
        {t('stayOnPage')}
      </Button>
      <Button
        className="btn-non-vbee-warning"
        variant="contained"
        onClick={handleConfirmLeave}
      >
        {t('leaveAnyway')}
      </Button>
    </StyledActionWarning>
  );

  const handleAddNewBlock = (
    blockId,
    blockData,
    isScrollToNewBlock = false,
  ) => {
    let newBlock = blockData;

    if (!blockData) {
      const newBlockId = uuidv4();
      const block = blocks[blockId];

      newBlock = [
        {
          emphasis: block?.emphasis,
          hasEmphasis: block?.hasEmphasis,
          voice: block?.voice,
          voiceCode: block?.voiceCode,
          speed: block?.speed,
          breakTime: block?.breakTime,
          elements: block?.elements,
          text: '',
          id: newBlockId,
          blockStatus: BLOCK_STATUS.DRAFT,
        },
      ];

      setTimeout(() => {
        dispatch(actions.project.setCurrentBlockId(newBlockId));
      }, 0);

      if (!blockData && isScrollToNewBlock) {
        setTimeout(() => {
          addBlockRef.current?.[blockId]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 100);
      }
    }

    if (
      currentFilteringStatusRef.current !== BLOCK_STATUS.ALL ||
      currentFilteringVoiceCodeRef.current !== ''
    ) {
      const newBlockIds = newBlock.map((block) => block.id);
      newBlockIds.forEach((id) => pendingBlockAdditions.current.add(id));
    }

    dispatch(actions.project.addBlock(blockId, newBlock));
  };

  const handleDuplicateBlock = (blockId) => {
    const sourceBlock = blocks[blockId];
    if (!sourceBlock) return;

    const newBlockId = uuidv4();
    const duplicatedBlock = [
      {
        emphasis: sourceBlock.emphasis,
        hasEmphasis: sourceBlock.hasEmphasis,
        voice: sourceBlock.voice,
        voiceCode: sourceBlock.voiceCode,
        speed: sourceBlock.speed,
        breakTime: sourceBlock.breakTime,
        elements: sourceBlock.elements
          ? JSON.parse(JSON.stringify(sourceBlock.elements))
          : sourceBlock.elements,
        text: sourceBlock.text,
        id: newBlockId,
        blockStatus: BLOCK_STATUS.DRAFT,
      },
    ];

    dispatch(actions.project.addBlock(blockId, duplicatedBlock));

    setTimeout(() => {
      dispatch(actions.project.setCurrentBlockId(newBlockId));
    }, 0);
  };

  const handleConfirmDeleteSentence = (blockId) => {
    if (currentBlockId === blockId) {
      const blockWithAudio = getBlockWithAudio(blockIds, blocks);
      const index = blockWithAudio.findIndex((block) => block.id === blockId);
      const nextBlockId = blockWithAudio[index + 1]?.id;
      // Set the audio resume block directly so that even if currentBlockId is
      // cleared later (e.g. by editor blur), play will still resume from nextBlock
      setAudioResumeBlock(nextBlockId);
      dispatch(actions.project.setCurrentBlockId(nextBlockId));
    }

    const lastBlockId = blockIds.length === 1;
    const lastBlockFilteredId = filteredBlockIds.length === 1 && !lastBlockId;
    if (lastBlockId || lastBlockFilteredId) {
      if (lastBlockId) {
        handleAddNewBlock();
      }
      dispatch(actions.project.setFilterStatus(BLOCK_STATUS.ALL));
      dispatch(actions.project.setFilterVoiceCode(''));
      dispatch(actions.project.setStartFilterBlock(true));
    }

    dispatch(actions.project.deleteBlock(blockId));
    dispatch(actions.project.unselectBlocks([blockId]));
    dispatch(actions.project.setIsPlaying(false));
  };

  // Handle merge block action - moved from ContentBlock for performance
  const handleMergeBlock = (blockId) => {
    const currentBlock = blocksRef.current[blockId];
    if (!currentBlock) return null;

    // Check if there's a next block
    const currentBlockIndex = blockIds.indexOf(blockId);
    const nextBlockId = blockIds[currentBlockIndex + 1];
    const nextBlock = blocksRef.current[nextBlockId];

    if (!nextBlock) return null;

    // Check if current block is synthesizing
    const isCurrentBlockSynthesizing =
      isDownloading || currentBlock.status === CONVERTING_STATUS.IN_PROGRESS;
    const isNextBlockSynthesizing =
      isDownloading || nextBlock.status === CONVERTING_STATUS.IN_PROGRESS;

    if (isCurrentBlockSynthesizing || isNextBlockSynthesizing) return null;

    // Merge text: block1 + newline + block2
    const mergedText =
      currentBlock.text && nextBlock.text
        ? `${currentBlock.text}\n${nextBlock.text}`
        : currentBlock.text || nextBlock.text || '';

    // Prepare merged block data
    const mergedBlockData = {
      text: mergedText,
      // Clear audio-related fields (from both blocks)
      status: null,
      blockStatus: BLOCK_STATUS.DRAFT,
    };

    // Update current block with merged content
    dispatch(actions.project.updateBlock(blockId, mergedBlockData));

    // Delete next block
    dispatch(actions.project.deleteBlock(nextBlockId));

    // Unselect if next block was selected
    dispatch(actions.project.unselectBlocks([nextBlockId]));

    // Focus on merged block
    setTimeout(() => {
      handleFocusingBlock(blockId);
    }, 100);

    return mergedText;
  };

  const handleReorder = (list, blockId, direction) => {
    const result = [...list];
    const currentIndex = result.indexOf(blockId);
    if (direction === DIRECTION.UP) {
      const [removed] = result.splice(currentIndex, 1);
      result.splice(currentIndex - 1, 0, removed);
    } else {
      const [removed] = result.splice(currentIndex, 1);
      result.splice(currentIndex + 1, 0, removed);
    }
    return result;
  };

  const handleDragEnd = (blockId, direction) => {
    if (!direction) {
      return;
    }

    const newBlockIds = handleReorder(blockIdsRef.current, blockId, direction);
    dispatch(actions.project.updateOrderBlock(newBlockIds));
  };
  const checkRouteChange = (nextLocation) => {
    const arrayPath = nextLocation.pathname.split('/');
    const isLeavingProject =
      (arrayPath[1] === 'projects' && !arrayPath[2]) ||
      arrayPath[1] !== 'projects';
    return isLeavingProject && (hasUnsavedWork || isProjectConverting);
  };
  const handleConfirmNonVbeeDialog = async () => {
    setOpenWrongSyntaxDialogV2(null);
  };
  const handleClickCharsFactorCheckbox = () => {
    if (!charsFactorCheckbox) {
      localStorage.setItem('nonVbeeDialog', true);
      localStorage.setItem('charsFactorDialog', true);
    } else {
      localStorage.removeItem('nonVbeeDialog');
      localStorage.removeItem('charsFactorDialog');
    }
    setCharsFactorCheckbox((value) => !value);
  };

  const handleClickBuyPackages = () => {
    history.push(ROUTES.PAYMENT);
  };
  const handleUpdateCredits = (idBlock, text, creditFactor) => {
    const credits = countTextLength(text) * (creditFactor || 1);
    dispatch(actions.project.updateCreditsBlocks(idBlock, credits));
  };

  const handleConvertBlockToAudio = async (block) => {
    // Legacy flow saves the whole project per block — this round-trip also
    // paces back-to-back synthesis requests (skipping it overloads the server
    // → 502s). Under auto-save the content is already persisted once per user
    // action at the queue entry point, so we skip the per-block save there.
    if (!autoSaveEnabled) {
      const response = await saveProject();
      if (!response.status) {
        dispatch(
          actions.project.updateBlockSynthesisInfo(block.id, {
            status: CONVERTING_STATUS.FAILURE,
          }),
        );
        return;
      }
    }

    dispatch(actions.project.updateOriginalBlocks(block.id));
    // Create data for a synthesis request
    const synthesisPayload = {
      text: block.text,
      voiceCode: block?.voiceCode || voiceDefault?.code,
      speed: block?.speed || DEFAULT_SPEED,
      projectId,
      blockId: block.id,
      audioType,
      ...(block?.hasEmphasis ? { emphasisIntensity: block?.emphasis } : {}),
    };

    const options = {};
    if (isUsingTurnstile) {
      options.turnstileToken = await turnstileWidgetRef.current.onVerify();
      turnstileWidgetRef.current.reset();
    }

    // Call API to synthesize
    const data = await apis.synthesis.createSynthesis(
      synthesisPayload,
      options,
    );
    if (data?.status && data.result?.requestId) {
      registerRequestMeta(data.result.requestId, {
        module: MOE_MODULE.PROJECT,
        voice: block?.voice || voiceDefault,
      });
      trackTryVoice({
        voice: pickSubmittedVoice(synthesisPayload.voiceCode, [
          block?.voice,
          voiceDefault,
        ]),
        action: TRY_VOICE_ACTION.CONVERT,
      });
    }
    if (!blockIdsRef.current.includes(block.id)) return;

    // Update block synthesis data
    const isCreatedReqSuccess = data?.status;
    dispatch(
      actions.project.updateBlockSynthesisInfo(block.id, {
        ...(isCreatedReqSuccess
          ? { requestId: data.result?.requestId }
          : { status: CONVERTING_STATUS.FAILURE }),
      }),
    );
    dispatch(actions.project.deleteCreditsBlocks(block.id));

    dispatch(
      actions.project.updateBlockStatus({
        blockId: block.id,
        blockStatus: isCreatedReqSuccess
          ? BLOCK_STATUS.IN_PROGRESS
          : BLOCK_STATUS.ERROR,
      }),
    );

    if (isCreatedReqSuccess) {
      const requestId = data.result?.requestId;
      updateTrialUsageAfterSuccess([block], requestId ? [requestId] : []);
      const isNotifyLowCredits = checkShouldNotifyLowCredits({
        beforeCredits: totalCredits,
        afterCredits: totalCredits - (countTextLength(block.text) || 0),
        packageCode: user.packageCode,
      });
      if (isNotifyLowCredits) setNotifyLowCreditsDialog(true);
    }

    if (!isCreatedReqSuccess) {
      const { errorMessage, errorCode } = data;
      if (block?.synthesisContext === SYNTHESIS_CONTEXT.MULTI_BLOCK) {
        if (errorCode === ERROR_CODE.VOICE_TRIAL_LIMIT_REACHED) {
          setFailedBlocksWithError((prev) => [...prev, block]);
        } else {
          dispatch(
            actions.noti.push({
              severity: 'error',
              message:
                errorCode === ERROR_CODE.BAD_REQUEST
                  ? 'errorOccurred'
                  : 'synthesisFailure',
              value: errorMessage,
            }),
          );
        }

        return;
      }
      switch (errorCode) {
        case ERROR_CODE.INVALID_SYNTAX: {
          setOpenWrongSyntaxDialog({
            name: 'error-syntax',
            variant: 'warning',
            title: t('errorSyntax'),
            description: t('errorSyntaxDescription'),
          });
          break;
        }

        case ERROR_CODE.SPAM_ACCOUNT:
        case ERROR_CODE.EXCEED_RATE_LIMIT: {
          setOpenWrongSyntaxDialogV2({
            name: 'error-syntax',
            variant: 'warning',
            title: t('cannotConvertTTS'),
            description: t('blockSpamEmailDesc'),
            type: DIALOG_V2_TYPE.BLOCK_SYNTHESIS,
          });
          break;
        }

        case ERROR_CODE.TEXT_TOO_LONG: {
          trackPaywall(MOE_PAYWALL_TYPE.OUT_OF_CREDITS, {
            trigger: MOE_PAYWALL_TRIGGER.SERVER_ERROR,
          });
          setOpenOutOfCreditsDialog(true);
          break;
        }

        case ERROR_CODE.LIMIT_FREE_INSTANT_VOICE_SYNTHESIS: {
          // TODO: Pass max instant synthesis to dialog
          dispatch(
            actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
              description: t('unlockUnlimitedInstantVoiceSynthesis'),
              img: CrownImg,
              classNameImg: 'custom-img',
              maxWidth: 'sm',
            }),
          );
          break;
        }

        case ERROR_CODE.INACTIVE_VOICE: {
          dispatch(
            actions.noti.push({
              severity: 'error',
              message: t('inactiveVoiceNoti', {
                voiceName: block?.voice?.name,
              }),
              value: errorMessage,
            }),
          );
          break;
        }

        case ERROR_CODE.NO_MORE_SUPPORT_VOICE: {
          dispatch(
            actions.noti.push({
              severity: 'error',
              message: t('inactiveVoiceNoti', {
                voiceName: block?.voice?.name,
              }),
              value: errorMessage,
            }),
          );
          break;
        }

        case ERROR_CODE.DISCARDED_VOICE_CLONING: {
          handleOpenDiscardedVoiceCloningDialog();
          break;
        }

        case ERROR_CODE.VOICE_TRIAL_LIMIT_REACHED: {
          const responseTrial = await apis.user.getTrialVoiceUsage();
          const latestTrialUsage =
            responseTrial?.result?.voiceUsage || stateTrialVoiceUsage;
          dispatch(actions.user.updateTrialVoiceUsage(latestTrialUsage));
          const exceededVoices = findExceededTrialVoicesForBlocks(
            [block],
            latestTrialUsage,
          );
          if (exceededVoices.length) {
            const exceededVoiceObjects = exceededVoices.map(
              (item) => item.voice,
            );
            handleOpenVoiceTrialLimitDialog(exceededVoiceObjects);
          } else {
            dispatch(
              actions.noti.push({
                severity: 'error',
                message:
                  errorCode === ERROR_CODE.BAD_REQUEST
                    ? 'errorOccurred'
                    : 'synthesisFailure',
                value: errorMessage,
              }),
            );
          }
          break;
        }

        default: {
          dispatch(
            actions.noti.push({
              severity: 'error',
              message:
                errorCode === ERROR_CODE.BAD_REQUEST
                  ? 'errorOccurred'
                  : 'synthesisFailure',
              value: errorMessage,
            }),
          );
        }
      }
    }
  };

  const processQueueSynthesis = async () => {
    const synthesisQueue = queueSynthesis.filter((block) =>
      blockIdsRef.current.includes(block.id),
    );
    if (synthesisQueue.length === 0 || isProcessing) return;

    setIsProcessing(true);
    const task = synthesisQueue[0];

    const isBlockSynthesisContextMultiBlock =
      task?.synthesisContext === SYNTHESIS_CONTEXT.MULTI_BLOCK;
    if (isBlockSynthesisContextMultiBlock) setIsSynthesisMultiBlock(true);

    try {
      await handleConvertBlockToAudio(task);
      setQueueSynthesis((prev) => prev.slice(1));
    } catch (error) {
      setQueueSynthesis((prev) => prev.slice(1));
    } finally {
      setIsProcessing(false);
      if (isBlockSynthesisContextMultiBlock) setIsSynthesisMultiBlock(false);
    }
  };

  const handleFetchProjectWithBlockSynthesisInfo = async () => {
    const selectedFields = [
      'blocks._id',
      'blocks.requestId',
      'blocks.status',
      'blocks.audioLink',
      'blocks.audioExpiredAt',
    ].join(',');
    const response = await apis.project.getProject(projectId, selectedFields);
    if (response.status) {
      const { project } = response.result;
      const newBlocks = project.blocks.reduce((acc, block) => {
        if (block.status === CONVERTING_STATUS.FAILURE) {
          acc[block.id] = {
            ...blocksRef.current[block.id],
            ...block,
            audioExpiredAt: null,
            status: CONVERTING_STATUS.FAILURE,
          };
          return acc;
        }

        // Check by the audio link to determine if the block result is changed and has success synthesized
        const isChangedAudioLink = block?.audioLink
          ? block?.audioLink !== blocks[block.id]?.audioLink
          : false;
        // Always update status from BE if BE status is IN_PROGRESS to ensure sync
        // This fixes the issue where socket updates block to SUCCESS but BE still has IN_PROGRESS
        const shouldUpdateStatusFromBE =
          isChangedAudioLink || block.status === CONVERTING_STATUS.IN_PROGRESS;
        // The condition helps to preserve the loading state of the block, ensure user experience in case the audio is expired
        // Get block from useRef to ensure that the function will always use the latest block data
        // FIX: Always update status from BE if BE status is IN_PROGRESS to prevent desync
        acc[block.id] = {
          ...blocksRef.current[block.id],
          ...block,
          audioExpiredAt: isChangedAudioLink
            ? block.audioExpiredAt
            : blocksRef.current[block.id]?.audioExpiredAt,
          status: shouldUpdateStatusFromBE
            ? block.status
            : blocksRef.current[block.id]?.status,
        };
        return acc;
      }, {});
      dispatch(actions.project.updateMultiBlockSynthesisStatus(newBlocks));
      dispatch(actions.project.setMultiBlockStatus(newBlocks));
    }
  };

  useEffect(() => {
    processQueueSynthesis();
  }, [queueSynthesis, isProcessing]);

  // Show popup for failed blocks with error 1045 after queue is processed
  useEffect(() => {
    const currentQueueSynthesis = queueSynthesis.filter((block) =>
      blockIdsRef.current.includes(block.id),
    );
    const isQueueEmpty = currentQueueSynthesis.length === 0;
    const isNotProcessing = !isSynthesisMultiBlock;
    if (
      isQueueEmpty &&
      isNotProcessing &&
      failedBlocksWithError.length > 0 &&
      enableVoiceTrial
    ) {
      // Fetch latest trial usage
      const showFailedBlocksDialog = async () => {
        try {
          const responseTrial = await apis.user.getTrialVoiceUsage();
          const latestTrialUsage =
            responseTrial?.result?.voiceUsage || stateTrialVoiceUsage;
          dispatch(actions.user.updateTrialVoiceUsage(latestTrialUsage));

          const exceededVoices = findExceededTrialVoicesForBlocks(
            failedBlocksWithError,
            latestTrialUsage,
          );

          if (exceededVoices.length) {
            const exceededVoiceObjects = exceededVoices.map(
              (item) => item.voice,
            );
            handleOpenVoiceTrialLimitDialog(exceededVoiceObjects);
          }
        } catch (error) {
          // Fallback to show dialog with available voices
          const fallbackVoices = failedBlocksWithError
            .map((block) => block?.voice)
            .filter(Boolean);
          if (fallbackVoices.length) {
            handleOpenVoiceTrialLimitDialog(fallbackVoices);
          }
        } finally {
          // Clear failed blocks after showing dialog
          setFailedBlocksWithError([]);
        }
      };

      showFailedBlocksDialog();
    }
  }, [
    queueSynthesis,
    isSynthesisMultiBlock,
    failedBlocksWithError,
    enableVoiceTrial,
    stateTrialVoiceUsage,
  ]);

  useEffect(() => {
    const projectWithBlockSynthesisInfoInterval = setInterval(() => {
      handleFetchProjectWithBlockSynthesisInfo();
    }, FETCH_BLOCKS_STATUS_INTERVAL);

    if (!isSynthesisInProgress) {
      clearInterval(projectWithBlockSynthesisInfoInterval);
    }

    return () => clearInterval(projectWithBlockSynthesisInfoInterval);
  }, [isSynthesisInProgress]);

  // Effect 1: Handle filter selection changes (re-filter blocks)
  useEffect(() => {
    const filterBlocks = () => {
      if (!startFilterBlock) return;

      const isNoFilter =
        currentFilteringStatus === BLOCK_STATUS.ALL &&
        currentFilteringVoiceCode === '';

      if (isNoFilter) {
        setFilteredBlockIds(blockIds);
        setFilterBlockResults(blocks);
        return;
      }

      // Helper: returns whether a block matches current filter status
      const matchesStatus = (blockId) => {
        if (currentFilteringStatus === BLOCK_STATUS.ALL) return true;
        const block = blocks[blockId];
        const originalBlock = originalBlocks[blockId];
        const blockStatus = getBlockStatus({
          block,
          originalBlock,
          audioType,
          maxCharLimit: maxCharLimitPerBlock,
          blackWordsList: blockBlackWordsMap[blockId] || [],
          voice: block?.voice || voiceDefault,
        });
        return blockStatus === currentFilteringStatus;
      };

      // Helper: returns whether a block matches current filter voice
      const matchesVoice = (blockId) => {
        if (currentFilteringVoiceCode === '') return true;

        return (
          (blocks[blockId]?.voice?.code || voiceDefault?.code) ===
          currentFilteringVoiceCode
        );
      };

      const filteredIds = blockIds.filter(
        (blockId) => matchesStatus(blockId) && matchesVoice(blockId),
      );

      // Create filtered blocks object from filteredIds
      const filteredBlocks = Object.fromEntries(
        filteredIds.map((id) => [id, blocks[id]]),
      );

      setFilteredBlockIds(filteredIds);
      setFilterBlockResults(filteredBlocks);

      const filteredBlockSelected = Object.keys(filteredBlocks)?.filter(
        (blockId) => selectedBlockIds?.includes(blockId),
      );
      const unSelectedBlockIds = selectedBlockIds?.filter(
        (blockId) => !filteredBlockSelected?.includes(blockId),
      );
      dispatch(actions.project.unselectBlocks(unSelectedBlockIds));

      dispatch(actions.project.setStartFilterBlock(false));
    };

    filterBlocks();
  }, [
    currentFilteringStatus, // Only trigger when filter selection changes
    startFilterBlock,
    currentFilteringVoiceCode,
    voiceDefault?.code,
    blocks,
    blockIds,
    selectedBlockIds,
  ]);

  // Effect 2: Update filtered block content when blocks are edited (without changing which blocks are filtered)
  useEffect(() => {
    const updateFilteredBlocks = () => {
      if (
        currentFilteringStatus === BLOCK_STATUS.ALL &&
        !currentFilteringVoiceCode
      ) {
        setFilteredBlockIds(blockIds);
        setFilterBlockResults(blocks);
        // Clear any pending additions when not filtering
        pendingBlockAdditions.current.clear();
        return;
      }

      // Handle pending block additions
      if (pendingBlockAdditions.current.size > 0) {
        const newIds = Array.from(pendingBlockAdditions.current);
        const validNewIds = newIds.filter((id) => blocks[id]); // Only include blocks that exist

        if (validNewIds.length > 0) {
          setFilteredBlockIds((prev) => {
            // Use the actual blockIds order from Redux to maintain correct positioning
            const newFiltered = blockIds.filter(
              (id) => prev.includes(id) || validNewIds.includes(id),
            );
            return newFiltered;
          });

          setFilterBlockResults((prev) => {
            const updated = { ...prev };
            validNewIds.forEach((blockId) => {
              updated[blockId] = blocks[blockId];
            });
            return updated;
          });

          // Clear processed additions
          validNewIds.forEach((id) => pendingBlockAdditions.current.delete(id));
        }
        return;
      }

      // Update the content of currently filtered blocks without changing which blocks are included
      const updatedFilteredBlocks = {};

      filteredBlockIds.forEach((blockId) => {
        if (blocks[blockId]) {
          updatedFilteredBlocks[blockId] = blocks[blockId];
        }
      });

      // Only update if we have valid blocks and the content has actually changed
      setFilterBlockResults((prev) => ({
        ...prev,
        ...updatedFilteredBlocks,
      }));

      // Handle case where blocks were deleted - remove them from filtered list
      const validFilteredIds = filteredBlockIds.filter(
        (blockId) => blocks[blockId],
      );
      if (validFilteredIds.length !== filteredBlockIds.length) {
        setFilteredBlockIds(validFilteredIds);
      }
    };

    updateFilteredBlocks();
  }, [
    blocks,
    blockIds,
    filteredBlockIds,
    currentFilteringStatus,
    currentFilteringVoiceCode,
    pendingBlockAdditions,
    setFilteredBlockIds,
    setFilterBlockResults,
  ]);

  useEffect(() => {
    dispatch(actions.project.setMultiBlockStatus(blocks));
  }, [audioType]);

  const handleAddBlockToQueue = async (block) => {
    dispatch(actions.project.setIsPlaying(false));
    dispatch(actions.project.setIsMultiBlockMode(false));
    dispatch(actions.project.setCurrentBlockId(null));
    if (currentPlayingBlockId === block?.id) {
      dispatch(actions.project.setIsPlayingBlock(false));
    }
    const { hasBlackWords, blackWordsFound } = checkBlockTextError(
      block?.text,
      maxCharLimitPerBlock,
      blackWordsList,
      block?.voice || voiceDefault,
    );
    if (hasBlackWords) {
      setBlockBlackWordsMap((prev) => ({
        ...prev,
        [block.id]: blackWordsFound,
      }));
      return;
    }
    if (!checkValidCreateSynthesis(block)) return;

    // Under auto-save only: persist pending edits once before enqueueing (the
    // per-block save in handleConvertBlockToAudio is skipped in that mode).
    // Legacy keeps its original behavior (saved per block downstream).
    if (autoSaveEnabled) {
      const saved = await ensureSavedBeforeSynthesis();
      if (!saved) {
        dispatch(
          actions.project.updateBlockSynthesisInfo(block.id, {
            status: CONVERTING_STATUS.FAILURE,
          }),
        );
        return;
      }
    }

    handleUpdateCredits(block.id, block.text, block.voice?.creditFactor);
    dispatch(
      actions.project.updateBlockSynthesisInfo(block.id, {
        status: CONVERTING_STATUS.IN_PROGRESS,
      }),
    );

    dispatch(actions.project.setIsProjectSynthesizing(false));
    setQueueSynthesis((prev) => [
      ...prev,
      { ...block, synthesisContext: SYNTHESIS_CONTEXT.SINGLE_BLOCK },
    ]);
  };

  const handleReSynthesizeBlock = (blockId) => {
    const block = blocks[blockId];
    if (!block) return;
    handleAddBlockToQueue(block);
  };

  const renderCloseButtonV2 = (handleClose) => {
    switch (openWrongSyntaxDialogV2?.type) {
      case DIALOG_V2_TYPE.NON_VBEE_WARNING:
        return (
          <div className="non-vbee-warning">
            <FormControlLabel
              control={
                <Checkbox
                  classes={{ root: 'checkbox' }}
                  color="iconPrimary"
                  value={charsFactorCheckbox}
                  onChange={handleClickCharsFactorCheckbox}
                />
              }
              label={
                <Typography className="not-show-again-text">
                  {t('notShowAgain')}
                </Typography>
              }
              labelPlacement={t('notShowAgain')}
            />
            <div className="">
              <Button
                className="btn-non-vbee-warning"
                variant="outlined"
                onClick={handleClose}
              >
                {t('cancel')}
              </Button>
              <Button
                className="btn-non-vbee-warning"
                variant="contained"
                onClick={handleConfirmNonVbeeDialog}
              >
                {t('continueGenerate')}
              </Button>
            </div>
          </div>
        );
      case DIALOG_V2_TYPE.CANNOT_USE_VOICE:
        return (
          <Button
            className="btn-understood"
            variant="contained"
            onClick={() => {
              setOpenWrongSyntaxDialogV2(null);
            }}
          >
            {t('gotItV2')}
          </Button>
        );
      case DIALOG_V2_TYPE.RENAME_VOICE:
        return (
          <Button
            className="btn-understood"
            variant="contained"
            onClick={() => {
              setOpenWrongSyntaxDialogV2(null);
            }}
          >
            {t('gotIt')}
          </Button>
        );
      case DIALOG_V2_TYPE.BLOCK_SYNTHESIS:
        return (
          <Button
            className="btn-understood"
            variant="contained"
            onClick={() => {
              window.open(`${LANDING_PAGE_URL}/support`, '_blank');
            }}
          >
            {t('submitSupportRequest')}
          </Button>
        );
      default:
        return (
          <Button
            className="btn-understood"
            variant="contained"
            onClick={handleClickBuyPackages}
          >
            {t('buyPackages')}
          </Button>
        );
    }
  };

  const renderLockedCycleButton = () => (
    <Button variant="contained" onClick={() => setOpenDialogLockedCycle(null)}>
      {t('understoodV3')}
    </Button>
  );
  const renderCloseButton = (handleClose) => (
    <Button variant="contained" onClick={handleClose}>
      {t('understood')}
    </Button>
  );

  const renderChangeFormatButton = () => (
    <div className="change-format-button">
      <Button
        className="btn btn-cancel"
        variant="outlined"
        onClick={() =>
          dispatch(actions.project.setOpenDialogChangeFormat(false))
        }
      >
        {t('cancel')}
      </Button>
      <Button
        className="btn btn-agree"
        variant="contained"
        onClick={() => {
          dispatch(
            actions.project.setAudioType(audioType === 'mp3' ? 'wav' : 'mp3'),
          );
          dispatch(actions.project.setOpenDialogChangeFormat(false));
          dispatch(actions.project.setIsPlaying(false));
          dispatch(actions.project.setIsMultiBlockMode(false));
          dispatch(actions.project.setIsPlayingBlock(false));
        }}
      >
        {t('agree')}
      </Button>
    </div>
  );

  useEffect(
    () => () => {
      pendingBlockAdditions.current.clear();
    },
    [],
  );

  useEffect(() => {
    if (!isBlockUIImprovement) return undefined;
    const fetchBlackWords = async () => {
      const data = await apis.blackWords.getBlackWords();
      if (data?.status) {
        setBlackWordsList(
          data.result.blackWords.map((bw) => bw.word?.toLowerCase()),
        );
      }
    };
    fetchBlackWords();
    return undefined;
  }, [isBlockUIImprovement]);

  // Handle delete block
  const blocksSelected = useMemo(
    () => selectedBlockIds.map((blockId) => blocks[blockId]),
    [blockIds, blocks, selectedBlockIds],
  );
  const isSynthesizing =
    deleteDialogData === true
      ? blocksSelected.some(
          (block) => block?.blockStatus === BLOCK_STATUS.IN_PROGRESS,
        )
      : blocks?.[deleteDialogData]?.blockStatus === BLOCK_STATUS.IN_PROGRESS;

  const handleUpdateProject = async (blockIdsDeleted) => {
    // Under auto-save the block deletion is already persisted by the engine (the
    // deleteBlock(s) dispatch triggers an auto-save pass), so this legacy full
    // save is redundant.
    if (autoSaveEnabled) return;
    try {
      // TODO: Handle check if there is any block that is error then disable saving project
      await updateProject(projectId, {
        title,
        blocks: multiBlock?.blockIds
          ?.filter((blockId) => !blockIdsDeleted?.includes(blockId))
          ?.map((blockId) => ({
            id: blockId,
            title: blocks[blockId].title,
            characters: countTextLength(blocks[blockId].text || ''),
            text: blocks[blockId].text || '',
            voiceCode: blocks[blockId]?.voiceCode || voiceDefault?.code,
            breakTime: blocks[blockId]?.breakTime || DEFAULT_BREAK_TIME,
            speed: blocks[blockId]?.speed || DEFAULT_SPEED,
            elements: blocks[blockId]?.elements,
            isSample: blocks[blockId]?.isSample || false,
            startTime: blocks[blockId]?.startTime || undefined,
            endTime: blocks[blockId]?.endTime || undefined,
            emphasisIntensity: blocks[blockId]?.emphasis,
          })),
        audioType,
      });
    } catch {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
    }
  };

  const handleDeleteBlockSelected = () => {
    const isDeleteAll = blockIds.length === selectedBlockIds?.length;
    const isDeleteAllFiltered =
      filteredBlockIds.length === selectedBlockIds?.length && !isDeleteAll;

    if (isDeleteAll || isDeleteAllFiltered) {
      if (isDeleteAll) {
        handleAddNewBlock();
      }
      dispatch(actions.project.setFilterStatus(BLOCK_STATUS.ALL));
      dispatch(actions.project.setFilterVoiceCode(''));
      dispatch(actions.project.setStartFilterBlock(true));
    }
    const blockIdsToDelete = selectedBlockIds;
    dispatch(actions.project.setCurrentBlockId(null));
    dispatch(actions.project.deleteBlocks(blockIdsToDelete));
    dispatch(actions.project.setIsPlaying(false));
    handleUpdateProject(blockIdsToDelete ?? []);
    setDeleteDialogData(false);
  };

  const handleDeleteSingleBlock = (blockId) => {
    handleConfirmDeleteSentence(blockId);
    handleUpdateProject([deleteDialogData]);
    setDeleteDialogData(false);
  };

  const handleDeleteMultiBlock = () => {
    if (deleteDialogData === true) {
      handleDeleteBlockSelected();
    } else {
      // Delete single block with id
      handleDeleteSingleBlock(deleteDialogData);
    }
  };

  const descriptionDeleteBlockKeyName = useMemo(() => {
    if (isSynthesizing) {
      return 'descriptionDeleteBlockPending';
    }

    if (deleteDialogData === true) {
      return selectedBlockIds.length === 1
        ? 'areYouSureWantToDeleteBlock'
        : 'areYouSureWantToDeleteBlocks';
    }
    return 'areYouSureWantToDeleteBlock';
  }, [isSynthesizing, selectedBlockIds]);

  const numberOfBlocksToDelete = useMemo(() => {
    if (deleteDialogData === true) {
      return selectedBlockIds.length === blockIds.length
        ? t('allBlocks')
        : selectedBlockIds.length;
    }
    return blockIds.length === 1 ? t('allBlocks') : 1;
  }, [deleteDialogData, selectedBlockIds]);

  // Calculate costs credit for all blocks without audio link
  const blockWithoutAudioLink = blockWithText.filter((block) =>
    [
      BLOCK_STATUS.DRAFT,
      BLOCK_STATUS.ERROR,
      BLOCK_STATUS.AUDIO_EXPIRED,
      BLOCK_STATUS.IN_PROGRESS,
    ]?.includes(block?.blockStatus),
  );

  const costsCredit = useMemo(
    () =>
      blockWithoutAudioLink.reduce(
        (acc, block) =>
          acc +
          (block?.voice?.creditFactor || voiceDefault?.creditFactor) *
            countTextLength(block?.text || ''),
        0,
      ),
    [blockWithoutAudioLink, voiceDefault],
  );

  const visibleBlockResults = useMemo(
    () =>
      Object.fromEntries(
        filteredBlockIds.map((id) => [id, filterBlockResults[id]]),
      ),
    [filteredBlockIds, filterBlockResults],
  );

  return (
    <>
      <Toolbar
        handleConvertBlockSelected={handleConvertBlockSelected}
        setDeleteDialog={setDeleteDialogData}
        filterBlockResults={visibleBlockResults}
      />
      <Divider sx={{ color: COLOR.indigo[16] }} />
      {autoSaveEnabled && <AutoSaveBanner />}
      {showExportBanner && (
        <ExportingBanner isExporting={isExporting} formats={exportFormats} />
      )}
      <StyledContent
        sidebarWidth={getSidebarWidth()}
        isShowHeaderBanner={isShowHeaderBanner}
        hasCharacterLimitBanner={isAutoSaveOverLimit}
        isFilteringBlockStatus={isFilteringBlockStatus || isFilteringBlockVoice}
        hasSelectedBlock={hasSelectedBlock}
        onClick={handleStopFocusOnBlock}
        role="button"
        tabIndex="-1"
      >
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
          role="button"
          tabIndex="-1"
          style={{
            height: `calc(100vh - ${
              isAutoSaveOverLimit ? CHARACTER_LIMIT_BANNER_HEIGHT : 0
            }px - ${HEADER_HEIGHT}px - ${
              isFilteringBlockStatus ||
              hasSelectedBlock ||
              isFilteringBlockVoice
                ? 0
                : AUDIO_PLAYER_HEIGHT
            }px - ${TOOLBAR_HEIGHT}px - ${
              isShowHeaderBanner ? FLASH_SALE_BANNER_HEIGHT : 0
            }px - ${showExportBanner ? EXPORT_BANNER_HEIGHT : 0}px)`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <VirtualizedContentList
            enableCreditUsagePreview={enableCreditUsagePreview}
            setDeleteDialogData={setDeleteDialogData}
            blockIds={filteredBlockIds}
            blocks={filterBlockResults}
            blocksFiltered={visibleBlockResults}
            originalBlocks={originalBlocks}
            originalBlockIds={originalBlockIds}
            isFiltering={isFilteringBlockStatus || isFilteringBlockVoice}
            containerHeight="100%"
            renderContentBlockProps={{
              audioType,
              characterExceed,
              onChangeCharacterExceed: handleChangeCharacterExceed,
              onAddNewParagraphs: handleAddNewBlock,
              onSelectParagraphId: handleConfirmDeleteSentence,
              onOpenDiscardedVoiceCloningDialog:
                handleOpenDiscardedVoiceCloningDialog,
              onFocusingBlock: handleFocusingBlock,
              onDragEnd: handleDragEnd,
              onSaveProject: saveProject,
              voiceDefault,
              isDownloading: isEditLocked,
              onOpenOutOfCreditsDialog: setOpenOutOfCreditsDialog,
              checkValidCreateSynthesis,
              currentBlockId,
              handlePlayAudio: handlePlayAudioInBlock,
              onOpenWrongSyntaxDialog: setOpenWrongSyntaxDialog,
              onOpenWrongSyntaxDialogV2: setOpenWrongSyntaxDialogV2,
              projectId,
              isPlayingBlock,
              currentPlayingBlockId,
              onConvertBlockToAudio: handleAddBlockToQueue,
              scrollToBlockId,
              isPlaying,
              onMergeBlock: handleMergeBlock,
              isBlockUIImprovement,
              onDuplicateBlock: handleDuplicateBlock,
              onReSynthesizeBlock: handleReSynthesizeBlock,
              blockAudioCurrentTime,
              blockAudioDuration,
              handleSeekedInBlock,
              blockBlackWordsMap,
            }}
            showAddButton={!isFilteringBlockStatus || !isFilteringBlockVoice}
            addButtonProps={{
              onClick: () =>
                handleAddNewBlock(
                  filteredBlockIds[filteredBlockIds?.length - 1],
                  null,
                  true,
                ),
              text: t('addMoreBlock'),
              icon: addBlockIcon,
            }}
          />
        </div>
      </StyledContent>

      {!isFilteringBlockStatus && !hasSelectedBlock && !isFilteringBlockVoice && (
        <div data-no-blur-block>
          <AudioPlayer
            enableCreditUsagePreview={enableCreditUsagePreview}
            costsCredit={costsCredit}
            isPlayAudio={
              isAudioLinkExist &&
              !!blockWithText.length &&
              !blockWithTextChanged?.length &&
              !hasAudioExpired
            }
            handleConvertAudio={handleConvertAudio}
            isConverting={isProjectConverting}
            isDisableConvertBtn={isDisableConvertBtn}
            isLoading={isLoading}
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={duration}
            handlePlay={handlePlay}
            handlePause={handlePause}
            handleSeeked={handleSeeked}
            title={title}
            blocks={blocks}
            handleChangeVolume={handleChangeVolume}
            onSaveProject={saveProjectBeforeAction}
            synthesisProgress={synthesisProgress}
            isDisabledBtnAudioEffects={isDisabledBtnAudioEffects}
          />
        </div>
      )}

      <NotificationDialogV3
        name="unsaved-changes"
        open={isOpenUnsavedChangesDialog}
        variant="none"
        title={t('unsavedChangesTitle')}
        padding="12px 24px"
        paddingHeader="8px 24px"
        description={t('unsavedChangesDescription')}
        onClose={() => setIsOpenUnsavedChangesDialog(false)}
        actionComponent={renderUnsavedChangesActions()}
      />
      <CustomActionLeavePage
        when={hasUnsavedWork}
        isAutoSave={autoSaveEnabled}
        navigate={(path) => {
          history.push(path);
        }}
        shouldBlockNavigation={checkRouteChange}
        visiblePopup={isOpenUnsavedChangesDialog}
        onLeavePage={handleConfirmLeave}
        onCancel={handleCloseUnsavedChangesDialog}
      />
      <CustomActionLeavePage
        isProjectConverting
        when={isProjectConverting}
        navigate={(path) => {
          history.push(path);
        }}
        shouldBlockNavigation={checkRouteChange}
        visiblePopup={isOpenUnsavedChangesDialog}
        onLeavePage={handleConfirmLeave}
        onCancel={handleCloseUnsavedChangesDialog}
      />
      <NotificationDialogV3
        name={openWrongSyntaxDialogV2?.name}
        title={openWrongSyntaxDialogV2?.title}
        description={openWrongSyntaxDialogV2?.description}
        subDescription={openWrongSyntaxDialogV2?.subDescription}
        variant={openWrongSyntaxDialogV2?.variant}
        open={!!openWrongSyntaxDialogV2?.title}
        onClose={() => setOpenWrongSyntaxDialogV2(null)}
        actionComponent={renderCloseButtonV2(() =>
          setOpenWrongSyntaxDialogV2(null),
        )}
      />
      {renderOutOfCreditsDialog()}
      {renderMaxCharsLimitExceededDialog()}
      {openMaxCharsExceededOnSave && (
        <NotificationDialogV4
          open
          onClose={() => setOpenMaxCharsExceededOnSave(false)}
          image={WaringImg}
          title={t('maxCharsLimitExceeded')}
          description={t('pronunciationCheckMaxCharsDesc', {
            maxLengthInputText: delimitNumber(MAX_TOTAL_CHARACTERS, language),
          })}
          actionComponent={
            <Button
              variant="contained"
              onClick={() => setOpenMaxCharsExceededOnSave(false)}
            >
              {t('understood')}
            </Button>
          }
        />
      )}

      {!!openDialogLockedCycle?.title && (
        <NotificationDialogV4
          image={LockCycle}
          title={openDialogLockedCycle?.title}
          description={openDialogLockedCycle?.description}
          actionComponent={renderLockedCycleButton()}
          open={!!openDialogLockedCycle?.title}
          onClose={() => setOpenDialogLockedCycle(null)}
        />
      )}

      <NotificationDialogV3
        name="download-format"
        title={t('noteV2')}
        description={t('desscriptionChangeFormat')}
        variant="warning"
        open={openDialogChangeFormat}
        onClose={() =>
          dispatch(actions.project.setOpenDialogChangeFormat(false))
        }
        actionComponent={renderChangeFormatButton()}
        padding="12px 32px"
      />

      <CommonDialog
        width="512px"
        title={t('confirmDelete')}
        fullWidth
        contentComponent={
          <Typography
            variant="body-16-regular"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: t(descriptionDeleteBlockKeyName, {
                value: numberOfBlocksToDelete,
              }),
            }}
          />
        }
        open={deleteDialogData}
        onClose={() => setDeleteDialogData(false)}
        actionComponent={
          <>
            <CommonButton
              customStyle={{
                padding: '8px 24px',
                borderRadius: '8px',
                height: '40px',
              }}
              variant="outlineSmall"
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.darkBlue,
              }}
              onClick={() => setDeleteDialogData(false)}
            >
              {t('cancel')}
            </CommonButton>
            <CommonButton
              customStyle={{
                padding: '8px 24px',
                backgroundColor: COLOR.error,
                borderRadius: '8px',
                height: '40px',
                '&:disabled': {
                  backgroundColor: COLOR.error,
                  opacity: 0.5,
                  cursor: 'not-allowed',
                  pointerEvents: 'auto',
                },
              }}
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.white,
              }}
              color="error"
              variant="contained"
              onClick={handleDeleteMultiBlock}
            >
              {t('delete')}
            </CommonButton>
          </>
        }
      />

      <NotificationDialog
        name={openWrongSyntaxDialog?.name}
        title={openWrongSyntaxDialog?.title}
        description={openWrongSyntaxDialog?.description}
        subDescription={openWrongSyntaxDialog?.subDescription}
        variant={openWrongSyntaxDialog?.variant}
        open={!!openWrongSyntaxDialog?.title}
        onClose={() => setOpenWrongSyntaxDialog(null)}
        actionComponent={renderCloseButton(() =>
          setOpenWrongSyntaxDialog(null),
        )}
      />
      <ExpiredPackageAlert
        open={openExpiredPackageDialog}
        onClose={handleCloseExpiredPackageDialog}
      />
      {notifyLowCreditsDialog && (
        <NotifyLowCredits
          open={notifyLowCreditsDialog}
          onClose={() => setNotifyLowCreditsDialog(false)}
          usingPackage={usingPackage}
        />
      )}

      {isUsingTurnstile && <TurnstileWidget ref={turnstileWidgetRef} />}
    </>
  );
};

export default Content;
